function formatAuditAction(action) {
  const labels = {
    "notification.status_updated": "Notification status updated",
    "member.prequalification_submitted": "Applicant submitted prequalification",
    "member.waiver_acknowledged": "Applicant acknowledged waiver",
    "member.approve": "Member approved",
    "event.created": "Event created",
    "event.updated": "Event updated",
    "event_registration.created": "Event registration created",
    "event_registration.public_created": "Public event registration created",
    "event_registration.updated": "Event registration updated",
    "event_registration.public_cancelled": "Public event registration cancelled",
    "event_registration.payment_fulfilled": "Event registration payment fulfilled",
    "event_attendance.recorded": "Event attendance recorded",
    "membership_signup_intent.fulfilled": "Membership payment fulfilled",
    "membership_signup_intent.created": "Membership signup started",
    "membership_config.updated": "Membership settings updated",
    "membership_type.updated": "Membership plan updated",
    "member.subscription_lifecycle_updated": "Membership subscription updated",
    "payment.checkout_completed": "Payment checkout completed",
    "resource.created": "Resource created",
    "resource.updated": "Resource updated",
    "resource_reservation.member_created": "Member reservation created",
    "resource_reservation.member_rescheduled": "Member reservation rescheduled",
    "resource_reservation.member_cancelled": "Member reservation cancelled",
    "range.created": "Range created",
    "range.profile_updated": "Range profile updated",
    "range.branding_updated": "Range branding updated",
    "range.lifecycle_updated": "Range lifecycle updated",
    "range.public_domain_reattached": "Public domain connected"
  };
  if (labels[action]) {
    return labels[action];
  }
  return String(action || "Activity")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAuditEntityType(value) {
  const labels = {
    MembershipSignupIntent: "Membership signup",
    MembershipConfig: "Membership settings",
    MembershipType: "Membership plan",
    ResourceReservation: "Reservation",
    PublicDomain: "Public website domain"
  };
  if (labels[value]) {
    return labels[value];
  }
  return String(value || "Record")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAuditValue(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadAuditReport(events) {
  const headers = ["Time", "Activity", "Record", "Details", "Actor"];
  const rows = events.map((event) => [
    event.createdAt ? new Date(event.createdAt).toLocaleString() : "Unknown time",
    formatAuditAction(event.action),
    compactAuditEntity(event),
    auditDetailText(event),
    auditActorLabel(event)
  ]);
  const body = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(","))
  ].join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "rangegrid-audit-report.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function isDemoAuditEvent(event = {}) {
  const haystack = [
    event.actorUserId,
    event.entityId,
    event.entityType,
    event.details?.email,
    event.details?.recipientEmail,
    event.details?.statusReason,
    event.details?.reason,
    event.details?.title,
    event.details?.name
  ].filter(Boolean).join(" ").toLowerCase();
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|qa-|codex|test event|workflow verification|chapter lead to owner|handoff note|reaudit|demo)/i.test(haystack);
}

function compactAuditEntity(event = {}) {
  const type = formatAuditEntityType(event.entityType || "Record");
  const label =
    event.details?.resourceName ||
    event.details?.eventTitle ||
    event.details?.title ||
    event.details?.name ||
    event.details?.email ||
    event.details?.recipientEmail ||
    "";
  return label ? `${type} / ${label}` : type;
}

function auditActorLabel(event = {}) {
  const actor = String(event.actorUserId || "").toLowerCase();
  if (!actor || actor === "system") {
    return "System";
  }
  if (/415bb5e0-1091-705a-f92a-296ec9353229/i.test(actor)) {
    return "Saint Paul chapter lead";
  }
  if (/013b9530-f031-700c-66ef-491515c43dfd/i.test(actor)) {
    return "Organization owner";
  }
  return "Workspace operator";
}

function auditDetailText(event) {
  if (event.action === "notification.status_updated") {
    return [
      event.details?.status ? `Status: ${formatAuditValue(event.details.status)}` : "",
      event.details?.recipientEmail ? `Recipient: ${event.details.recipientEmail}` : "",
      event.details?.type ? `Message: ${formatAuditValue(event.details.type)}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action?.startsWith("event.")) {
    return [
      event.details?.status ? `Status: ${formatAuditValue(event.details.status)}` : "",
      event.details?.visibility ? `Visibility: ${formatAuditValue(event.details.visibility)}` : "",
      event.details?.startsAt ? `Starts: ${new Date(event.details.startsAt).toLocaleString()}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action?.startsWith("event_registration.")) {
    return [
      event.details?.email ? `Email: ${event.details.email}` : "",
      event.details?.previousStatus ? `Previous: ${formatAuditValue(event.details.previousStatus)}` : "",
      event.details?.status ? `Status: ${formatAuditValue(event.details.status)}` : "",
      event.details?.paymentStatus ? `Payment: ${formatAuditValue(event.details.paymentStatus)}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action === "event_attendance.recorded") {
    return [
      event.details?.email ? `Email: ${event.details.email}` : "",
      event.details?.status ? `Status: ${formatAuditValue(event.details.status)}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action === "payment.checkout_completed") {
    return [
      event.details?.paymentStatus ? `Payment: ${formatAuditValue(event.details.paymentStatus)}` : "",
      event.details?.mode ? `Mode: ${formatAuditValue(event.details.mode)}` : "",
      event.details?.membershipTypeId ? `Plan: ${formatAuditValue(event.details.membershipTypeId)}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action === "membership_signup_intent.fulfilled") {
    return [
      event.details?.membershipTypeId ? `Plan: ${formatAuditValue(event.details.membershipTypeId)}` : "",
      event.details?.paymentStatus ? `Payment: ${formatAuditValue(event.details.paymentStatus)}` : ""
    ].filter(Boolean).join(" / ");
  }
  if (event.action === "member.subscription_lifecycle_updated") {
    return [
      event.details?.subscriptionStatus ? `Subscription: ${formatAuditValue(event.details.subscriptionStatus)}` : "",
      event.details?.paymentStatus ? `Payment: ${formatAuditValue(event.details.paymentStatus)}` : "",
      event.details?.status ? `Status: ${formatAuditValue(event.details.status)}` : ""
    ].filter(Boolean).join(" / ");
  }
  return "";
}

export function AuditPage({
  auditEvents,
  auditFilters,
  auditLoading,
  exportingAudit,
  selectedRange,
  setAuditFilters
}) {
  if (!selectedRange) {
    return null;
  }
  const visibleAuditEvents = (auditEvents || []).filter((event) => !isDemoAuditEvent(event));
  const hiddenDemoAuditCount = Math.max(0, (auditEvents || []).length - visibleAuditEvents.length);

  return (
    <section className="panel page-panel">
      <div className="panel-header">
        <h2>Audit</h2>
        <p>Recent activity for {selectedRange.name}.</p>
      </div>

      <div className="workspace-filters">
        <label>
          Action
          <input
            value={auditFilters.action}
            placeholder="Reservation created"
            onChange={(event) => setAuditFilters((current) => ({ ...current, action: event.target.value }))}
          />
        </label>
        <label>
          Entity type
          <input
            value={auditFilters.entityType}
            placeholder="Member, resource, class"
            onChange={(event) => setAuditFilters((current) => ({ ...current, entityType: event.target.value }))}
          />
        </label>
        <label>
          Actor
          <input
            value={auditFilters.actorUserId}
            placeholder="Owner or staff"
            onChange={(event) => setAuditFilters((current) => ({ ...current, actorUserId: event.target.value }))}
          />
        </label>
        <label>
          Search
          <input
            value={auditFilters.q}
            placeholder="Search details"
            onChange={(event) => setAuditFilters((current) => ({ ...current, q: event.target.value }))}
          />
        </label>
        <button type="button" onClick={() => downloadAuditReport(visibleAuditEvents)} disabled={exportingAudit || auditLoading}>
          {exportingAudit ? "Exporting..." : "Export audit CSV"}
        </button>
      </div>

      {auditLoading && <div className="empty-state">Loading audit events...</div>}
      {!auditLoading && hiddenDemoAuditCount > 0 && (
        <div className="access-demo-filter-note">
          {hiddenDemoAuditCount} non-public audit entr{hiddenDemoAuditCount === 1 ? "y" : "ies"} hidden from this view.
        </div>
      )}
      {!auditLoading && visibleAuditEvents.length === 0 && (
        <div className="empty-state">No audit events yet.</div>
      )}
      {!auditLoading && visibleAuditEvents.length > 0 && (
        <div className="audit-event-list">
          {visibleAuditEvents.map((event) => (
            <article className="audit-event-row" key={event.auditId}>
              <div>
                <strong>{formatAuditAction(event.action)}</strong>
                <span>{compactAuditEntity(event)}</span>
                {auditDetailText(event) && <span>{auditDetailText(event)}</span>}
              </div>
              <div>
                <span>{auditActorLabel(event)}</span>
                <span>{event.createdAt ? new Date(event.createdAt).toLocaleString() : "Unknown time"}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
